/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import {
	Box,
	Button,
	Chip,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	CircularProgress,
	useTheme,
	useMediaQuery,
} from '@mui/material';
import { OpenInNew } from '@mui/icons-material';
import {
	CreditCardInquiryTypeEnum,
	EmissionsRequestTypeEnum,
	IOperationalRequestResponse,
	OperationalRequestCategoryEnum,
	OperationsRequestTypeEnum,
	RefundRequestTypeEnum,
} from 'features/salesOrder/types';
import { getOperationalRequests } from 'features/salesOrder/services';
import { formatDateTimeToShowUser, getDetailOfCaseInCRM } from 'features/common/helpers';
import {
	CREDIT_CARD_INQUIRY_TYPE_LABELS,
	EMISSIONS_REQUEST_TYPE_LABELS,
	OPERATIONS_REQUEST_TYPE_LABELS,
	REFUND_TYPE_META,
} from '../../../../../features/salesOrder/constants';

interface OperationalRequestsListProps {
	saleOrderId: string;
}

const getRequestCategoryLabel = (category: OperationalRequestCategoryEnum): string => {
	switch (category) {
		case OperationalRequestCategoryEnum.Operations:
			return 'Operaciones';
		case OperationalRequestCategoryEnum.Emissions:
			return 'Emisiones';
		case OperationalRequestCategoryEnum.Refunds:
			return 'Devoluciones';
		case OperationalRequestCategoryEnum.CreditCardInquiry:
			return 'Consulta Tarjeta';
		default:
			return category;
	}
};
const getRequestTypeLabel = (type: string, category: OperationalRequestCategoryEnum): string => {
	if (category === OperationalRequestCategoryEnum.Operations) {
		return type in OPERATIONS_REQUEST_TYPE_LABELS
			? OPERATIONS_REQUEST_TYPE_LABELS[type as OperationsRequestTypeEnum]
			: type;
	}

	if (category === OperationalRequestCategoryEnum.Emissions) {
		return type in EMISSIONS_REQUEST_TYPE_LABELS
			? EMISSIONS_REQUEST_TYPE_LABELS[type as EmissionsRequestTypeEnum]
			: type;
	}

	if (category === OperationalRequestCategoryEnum.Refunds) {
		return type in REFUND_TYPE_META ? REFUND_TYPE_META[type as RefundRequestTypeEnum].label : type;
	}

	if (category === OperationalRequestCategoryEnum.CreditCardInquiry) {
		return type in CREDIT_CARD_INQUIRY_TYPE_LABELS
			? CREDIT_CARD_INQUIRY_TYPE_LABELS[type as CreditCardInquiryTypeEnum]
			: type;
	}

	return type;
};

export const OperationalRequestsList: React.FC<OperationalRequestsListProps> = ({ saleOrderId }) => {
	const [requests, setRequests] = useState<IOperationalRequestResponse[]>([]);
	const [loading, setLoading] = useState(true);
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));

	useEffect(() => {
		const fetchRequests = async () => {
			try {
				setLoading(true);
				const response = await getOperationalRequests(saleOrderId);
				setRequests(response.data);
			} catch (error) {
				console.error('Error fetching operational requests:', error);
			} finally {
				setLoading(false);
			}
		};

		fetchRequests();
	}, [saleOrderId]);

	const handleOpenCase = (request: IOperationalRequestResponse) => {
		const caseUrl = getDetailOfCaseInCRM(request.caseId);
		window.open(caseUrl, '_blank');
	};

	if (loading) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
				<CircularProgress />
			</Box>
		);
	}

	if (requests.length === 0) {
		return null;
	}

	return (
		<Box>
			<Box sx={{ overflowX: 'auto', overflowY: 'auto', maxHeight: isMobile ? 260 : 360 }}>
				<Table sx={{ minWidth: isMobile ? 400 : 650 }}>
					<TableHead>
						<TableRow>
							<TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>Categoría</TableCell>
							<TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>Tipo</TableCell>
							<TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>Fecha</TableCell>
							<TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>Creado por</TableCell>
							<TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>Caso</TableCell>
							<TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>Acción</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{requests.map((request) => (
							<TableRow key={request.id}>
								<TableCell>
									<Chip
										label={getRequestCategoryLabel(request.requestCategory)}
										size="small"
										color="primary"
										variant="outlined"
									/>
								</TableCell>
								<TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
									{getRequestTypeLabel(request.requestType, request.requestCategory)}
								</TableCell>
								<TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
									{formatDateTimeToShowUser(request.createdAt).substring(0, 16)}
								</TableCell>
								<TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>{request.createdBy}</TableCell>
								<TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>#{request.caseId}</TableCell>
								<TableCell>
									<Button
										size="small"
										variant="outlined"
										startIcon={<OpenInNew />}
										onClick={() => handleOpenCase(request)}
									>
										Abrir
									</Button>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</Box>
		</Box>
	);
};
